import {
	CommunityPostOptionsModel,
	CommunityViewModel,
	getNewPostViewModel
} from "client/Api/Communities/communityPosts";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useDataInputDialog,
	useSynchronizedRef,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	ReadonlyRefObject,
	useCallback,
	useEffect,
	useState
} from "react";
import { Dialog, FormattedText } from "client/Components";
import { CommunityPostDetailsViewModel } from "client/Api/Communities/communityPostViewer";
import { ComposerDialog } from "./ComposerDialog";

export type CreateCommunityPostDialogProps = {
	onSaved: (post: CommunityPostDetailsViewModel) => void;
	onClose: () => void;
};

export const CreateCommunityPostDialog = (props: CreateCommunityPostDialogProps): ReactElement => {
	const {
		onClose,
		onSaved
	} = props;

	const [state, setState] = useState<State>({
		status: "loading"
	});

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const alertManager: AlertManager = useAlerts();
	const onCloseRef: ReadonlyRefObject<() => void> = useSynchronizedRef(onClose);

	useEffect(
		(): (() => void) => {
			async function fetch(signal: AbortSignal): Promise<void> {
				setState({ status: "loading" });

				try {
					const {
						options,
						postableCommunities
					} = await getNewPostViewModel(signal);

					setState({
						status: "ready",
						options,
						communities: postableCommunities
					});
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					setState({ status: "error" });
					onCloseRef.current();
				}
			}

			const cancellation = new AbortController();
			fetch(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[alertManager, onCloseRef]
	);

	const saveHandler = useCallback(
		(post: CommunityPostDetailsViewModel): void => {
			alertManager.showSuccess(
				<FormattedText
					text={localize("_PostHasSuccessfullyBeenCreatedInName")}
					thingsToReplace={[
						{
							what: "{{Name}}",
							filler: post
								.community
								.name
						}
					]}
				/>
			);

			onSaved(post);
		},
		[alertManager, onSaved]
	);

	return (
		<>
			{state.status === "loading" && (
				<Dialog
					size={isMobile ? "large" : "long"}
					hasBusyIndicator
					height="fill"
					trackingId="create-community-post-loading-dialog"
				/>
			)}

			{state.status === "ready" && (
				<ComposerDialog
					mode={{
						kind: "create",
						communities: state.communities,
						communityId: null
					}}
					onClose={onClose}
					onSaved={saveHandler}
					options={state.options}
				/>
			)}
		</>
	);
};
CreateCommunityPostDialog.displayName = "CreateCommunityPostDialog";

type State = {
	status: "loading" | "error";
} | {
	status: "ready";
	options: CommunityPostOptionsModel;
	communities: readonly CommunityViewModel[];
};

export function useCreateCommunityPostDialog(): [
	element: ReactElement | null,
	open: (props: {}) => Promise<TryResult<CommunityPostDetailsViewModel>>
] {
	return useDataInputDialog(
		(_, onClose, onSave) => (
			<CreateCommunityPostDialog
				onSaved={onSave}
				onClose={onClose}
			/>
		)
	);
}